import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Styled Components
const Wrapper = styled.div`
  padding: 1rem;
  background: #f9f9f9;
  height: 85vh;
  position: relative;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  z-index: 10;

  &:hover { color: #000; }

  svg { width: 20px; height: 20px; }
`;

const dayNamesPL = [
  "Niedziela","Poniedziałek","Wtorek","Środa","Czwartek","Piątek","Sobota"
];

const monthNamesPL = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
];

const AverageSalesByWeekday = ({ data }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartData, setChartData] = useState(null);

  const calculateSales = (row) =>
    parseFloat(
      row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22
    ) || 0;

  // Init years
  useEffect(() => {
    const years = Array.from(
      new Set(
        data.map((row) => parseInt(row.Rok, 10)).filter((y) => !isNaN(y))
      )
    ).sort((a, b) => a - b);

    setAvailableYears(years);
    if (!selectedYear && years.length > 0) {
      setSelectedYear(years[years.length - 1]);
    }
  }, [data]);

  // Init months for selected year
  useEffect(() => {
    if (!selectedYear) return;

    const months = Array.from(
      new Set(
        data
          .filter((row) => parseInt(row.Rok, 10) === selectedYear)
          .map((row) => parseInt(row.Miesiac, 10))
          .filter((m) => !isNaN(m))
      )
    ).sort((a, b) => a - b);

    setAvailableMonths(months);
    if (!selectedMonth && months.length > 0) {
      setSelectedMonth(months[0]);
    }
  }, [selectedYear, data]);

  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    // aggregate totals and counts per weekday for both years
    const totals = Array(7).fill(0);
    const counts = Array(7).fill(0);
    const totalsPrev = Array(7).fill(0);
    const countsPrev = Array(7).fill(0);

    let grandTotal = 0, grandCount = 0; // for overall average (current year)

    data.forEach((row) => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      if (month !== selectedMonth) return;

      const date = new Date(row.DataWystawienia);
      if (isNaN(date)) return;
      const weekday = date.getDay();
      const sale = calculateSales(row);

      if (year === selectedYear) {
        totals[weekday] += sale;
        counts[weekday] += 1;
        grandTotal += sale;
        grandCount += 1;
      } else if (year === selectedYear - 1) {
        totalsPrev[weekday] += sale;
        countsPrev[weekday] += 1;
      }
    });

    const averages = totals.map((sum, idx) => (counts[idx] > 0 ? sum / counts[idx] : 0));
    const averagesPrev = totalsPrev.map((sum, idx) => (countsPrev[idx] > 0 ? sum / countsPrev[idx] : null));
    const avgOverall = grandCount > 0 ? grandTotal / grandCount : 0;

    // palette (your standard)
    const GREEN  = "#2e7d32"; // current year line
    const ORANGE = "#fb8c00"; // previous year line
    const BLUE   = "#1976d2"; // avg/reference line

    setChartData({
      labels: dayNamesPL,
      datasets: [
        {
          label: `Średnia ${selectedYear}`,
          data: averages,
          borderColor: GREEN,
          backgroundColor: "rgba(46,125,50,0.15)",
          fill: false,
          tension: 0.3,
          pointBackgroundColor: GREEN,
          pointRadius: 3,
        },
        {
          label: `Średnia ${selectedYear - 1}`,
          data: averagesPrev,
          borderColor: ORANGE,
          backgroundColor: "rgba(251,140,0,0.15)",
          fill: false,
          tension: 0.3,
          pointBackgroundColor: ORANGE,
          pointRadius: 3,
        },
        {
          label: `Średnia (linia) ${selectedYear}`,
          data: Array(7).fill(avgOverall),
          borderColor: BLUE,
          borderDash: [6, 4],
          fill: false,
          tension: 0,
          pointRadius: 0,
        },
      ],
    });
  }, [selectedYear, selectedMonth, data]);

  return (
    <Wrapper>
      <InfoIconWrapper data-tooltip-id="avg-weekly-lines-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="avg-weekly-lines-tooltip"
        place="left"
        content={
          <>
            <strong>Porównianie średnich tygodniowych sprzedaży</strong><br />
            Dwie linie: {selectedYear} (zielona) vs {selectedYear - 1} (pomarańczowa).<br />
            Niebieska linia = średnia ogólna ({selectedYear}).
          </>
        }
      />

      <FiltersWrapper>
        <label>
          Rok:&nbsp;
          <select
            value={selectedYear || ""}
            onChange={(e) => {
              setSelectedYear(parseInt(e.target.value, 10));
              setSelectedMonth(null);
            }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </label>

        <label>
          Miesiąc:&nbsp;
          <select
            value={selectedMonth || ""}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
          >
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {monthNamesPL[month - 1]}
              </option>
            ))}
          </select>
        </label>
      </FiltersWrapper>

      {chartData && (
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: `Porównianie średnich tygodniowych sprzedaży (${monthNamesPL[selectedMonth - 1]} ${selectedYear})`,
              },
              legend: { position: "top" },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const val = ctx.parsed.y ?? 0;
                    return `${ctx.dataset.label}: ${val.toLocaleString("pl-PL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Wartość" },
                ticks: {
                  callback: (value) =>
                    Number(value)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, " "),
                },
              },
            },
          }}
        />
      )}
    </Wrapper>
  );
};

export default AverageSalesByWeekday;
